import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  platformEmail,
  type PlatformEmailDelivery,
  type PlatformEmailKind,
  type PlatformEmailSecurity,
  type PlatformEmailSettings,
  type PlatformEmailTestResult,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { ajudaDoEmailDaPlataforma } from '@/shared/help/ajuda/platform';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { Field } from '@/shared/ui/Field';
import { FieldLabel } from '@/shared/ui/FieldLabel';
import { Select } from '@/shared/ui/Select';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

/** Porta usual de cada seguranca. */
const portaDaSeguranca: Record<PlatformEmailSecurity, number> = {
  ssl_implicito: 465,
  starttls: 587,
};

const segurancas: { value: PlatformEmailSecurity; label: string }[] = [
  { value: 'ssl_implicito', label: 'SSL implicito (porta 465)' },
  { value: 'starttls', label: 'STARTTLS (porta 587)' },
];

const tiposDeEmail: Record<PlatformEmailKind, string> = {
  redefinicao_senha: 'Redefinicao de senha',
  convite: 'Convite de acesso',
  autocadastro: 'Link do autocadastro',
  ativacao: 'Ativacao do cadastro publico',
  boas_vindas: 'Boas-vindas',
  teste: 'Teste',
};

interface Formulario {
  enabled: boolean;
  host: string;
  port: string;
  security: PlatformEmailSecurity;
  username: string;
  from_address: string;
  from_name: string;
  reply_to: string;
  timeout_seconds: string;
}

function formularioDe(atual: PlatformEmailSettings): Formulario {
  return {
    enabled: atual.enabled,
    host: atual.host,
    port: String(atual.port || portaDaSeguranca[atual.security]),
    security: atual.security,
    username: atual.username,
    from_address: atual.from_address,
    from_name: atual.from_name,
    reply_to: atual.reply_to,
    timeout_seconds: String(atual.timeout_seconds),
  };
}

/**
 * Configuracoes > E-mail da administracao da plataforma (ADR 0096).
 *
 * O servidor SMTP e a conta de onde saem todos os e-mails da plataforma: redefinicao de
 * senha, convite, link do autocadastro, ativacao do cadastro publico e boas-vindas. A
 * senha e so escrita: a tela diz se ha senha definida e permite trocar, e nunca a
 * mostra. O teste envia uma mensagem pela configuracao gravada e mostra o erro legivel.
 */
export function PlatformEmailSection() {
  const consulta = useQuery({
    queryKey: ['platform', 'email'],
    queryFn: ({ signal }) => platformEmail.get(signal),
  });

  if (consulta.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size={24} label="Carregando a configuracao de e-mail" />
      </div>
    );
  }
  if (consulta.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel carregar a configuracao de e-mail">
        {consulta.error.message}
      </Alert>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      <SituacaoDoEnvio atual={consulta.data} />
      <FormularioDoEmail
        key={consulta.data.updated_at ?? 'sem-configuracao'}
        atual={consulta.data}
      />
      <TesteDeEnvio atual={consulta.data} />
      <UltimosEnvios />
    </div>
  );
}

function SituacaoDoEnvio({ atual }: { atual: PlatformEmailSettings }) {
  return (
    <Card>
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-ink-900">Envio de e-mail</span>
          {atual.active ? (
            <Badge tone="success">Envio ativo</Badge>
          ) : atual.enabled ? (
            <Badge tone="warning">Ligado, sem enviar</Badge>
          ) : (
            <Badge tone="neutral">Envio desligado</Badge>
          )}
          <span className="text-xs text-ink-500">
            {atual.last_success_at
              ? `Ultimo teste bem-sucedido em ${formatDateTime(atual.last_success_at)}.`
              : 'Nenhum teste bem-sucedido ainda.'}
          </span>
        </div>
        {atual.problem ? (
          <Alert tone="warning" title="O envio esta ligado, mas nenhum e-mail sai">
            {atual.problem}
          </Alert>
        ) : null}
        {!atual.secrets_key_configured ? (
          <Alert tone="warning" title="Chave de segredos ausente">
            O servidor nao tem a chave de segredos da aplicacao
            (SEGURACORE_SECRETS_KEY): a senha da conta de e-mail nao pode ser gravada.
          </Alert>
        ) : null}
        {!atual.active ? (
          <p className="text-xs text-ink-500">
            Com o envio desligado, nenhum e-mail sai: os links de convite e de troca de
            senha ficam com quem administra, e o cadastro publico de corretores fica
            indisponivel.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function FormularioDoEmail({ atual }: { atual: PlatformEmailSettings }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const idDoLigado = useId();
  const [form, setForm] = useState<Formulario>(() => formularioDe(atual));
  const [senha, setSenha] = useState('');

  const set = <K extends keyof Formulario>(campo: K, valor: Formulario[K]) =>
    setForm((anterior) => ({ ...anterior, [campo]: valor }));

  const salvar = useMutation({
    mutationFn: () =>
      platformEmail.save({
        enabled: form.enabled,
        host: form.host.trim(),
        port: Number(form.port) || 0,
        security: form.security,
        username: form.username.trim(),
        ...(senha ? { password: senha } : {}),
        from_address: form.from_address.trim(),
        from_name: form.from_name.trim(),
        reply_to: form.reply_to.trim(),
        timeout_seconds: Number(form.timeout_seconds) || 0,
      }),
    onSuccess: (gravada) => {
      toast.success(
        gravada.active
          ? 'Configuracao gravada. Os e-mails da plataforma saem por ela.'
          : 'Configuracao gravada.',
      );
      setSenha('');
      queryClient.setQueryData(['platform', 'email'], gravada);
      void queryClient.invalidateQueries({ queryKey: ['platform', 'email'] });
    },
  });

  const erro = salvar.error instanceof ApiError ? salvar.error : null;
  const campos = [
    'host',
    'port',
    'security',
    'username',
    'password',
    'from_address',
    'from_name',
    'reply_to',
    'timeout_seconds',
  ];
  const erroGeral =
    salvar.isError && !campos.some((campo) => fieldErrorOf(erro, campo))
      ? salvar.error.message
      : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  function trocaSeguranca(valor: string) {
    const nova = valor as PlatformEmailSecurity;
    const portaAtual = Number(form.port);
    const usual = Object.values(portaDaSeguranca).includes(portaAtual);
    setForm((anterior) => ({
      ...anterior,
      security: nova,
      // A porta usual acompanha a seguranca; a porta escolhida a mao fica.
      port: !anterior.port || usual ? String(portaDaSeguranca[nova]) : anterior.port,
    }));
  }

  return (
    <Card>
      <CardHeader
        title="Servidor e remetente"
        description="Os e-mails da plataforma saem desta conta: redefinicao de senha, convite, link do autocadastro, ativacao do cadastro publico e boas-vindas."
      />
      <CardBody>
        <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
          {erroGeral ? (
            <Alert tone="danger" title="Nao foi possivel gravar">
              {erroGeral}
            </Alert>
          ) : null}
          <div className="flex items-start gap-2.5">
            <input
              id={idDoLigado}
              type="checkbox"
              role="switch"
              aria-checked={form.enabled}
              className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
              checked={form.enabled}
              onChange={(evento) => set('enabled', evento.target.checked)}
            />
            <div className="flex flex-col gap-0.5">
              <FieldLabel
                htmlFor={idDoLigado}
                label="Envio de e-mail ligado"
                className="text-sm text-ink-800"
                help={false}
              />
              <span className="text-xs text-ink-500">
                Desligado, nenhum e-mail sai. Confira com o e-mail de teste antes de
                ligar.
              </span>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-[2fr_1fr_1.4fr]">
            <Field
              label="Servidor"
              value={form.host}
              placeholder="Ex.: mail.exemplo.com.br"
              autoComplete="off"
              help={ajudaDoEmailDaPlataforma.Servidor}
              error={fieldErrorOf(erro, 'host')}
              onChange={(evento) => set('host', evento.target.value)}
            />
            <Field
              label="Porta"
              type="number"
              inputMode="numeric"
              value={form.port}
              help={false}
              error={fieldErrorOf(erro, 'port')}
              onChange={(evento) => set('port', evento.target.value)}
            />
            <Select
              label="Seguranca"
              value={form.security}
              options={segurancas}
              help={ajudaDoEmailDaPlataforma.Seguranca}
              error={fieldErrorOf(erro, 'security')}
              onChange={(evento) => trocaSeguranca(evento.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Usuario"
              value={form.username}
              autoComplete="off"
              help={false}
              hint="Em geral, o proprio endereco do remetente."
              error={fieldErrorOf(erro, 'username')}
              onChange={(evento) => set('username', evento.target.value)}
            />
            <Field
              label="Senha"
              type="password"
              value={senha}
              autoComplete="new-password"
              placeholder={
                atual.password_set
                  ? 'Senha definida. Deixe em branco para manter.'
                  : 'Informe a senha da conta'
              }
              help={ajudaDoEmailDaPlataforma.Senha}
              hint={
                atual.password_set
                  ? 'Senha definida. Ela nunca e mostrada; informe outra so para trocar.'
                  : 'Nenhuma senha gravada.'
              }
              error={fieldErrorOf(erro, 'password')}
              onChange={(evento) => setSenha(evento.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Endereco do remetente"
              type="email"
              value={form.from_address}
              placeholder="Ex.: suporte@seguracore.com.br"
              help={false}
              error={fieldErrorOf(erro, 'from_address')}
              onChange={(evento) => set('from_address', evento.target.value)}
            />
            <Field
              label="Nome do remetente"
              value={form.from_name}
              placeholder="SeguraCore"
              help={false}
              error={fieldErrorOf(erro, 'from_name')}
              onChange={(evento) => set('from_name', evento.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Responder para"
              type="email"
              value={form.reply_to}
              placeholder="Opcional"
              help={false}
              error={fieldErrorOf(erro, 'reply_to')}
              onChange={(evento) => set('reply_to', evento.target.value)}
            />
            <Field
              label="Prazo de envio (segundos)"
              type="number"
              inputMode="numeric"
              value={form.timeout_seconds}
              help={ajudaDoEmailDaPlataforma['Prazo de envio (segundos)']}
              error={fieldErrorOf(erro, 'timeout_seconds')}
              onChange={(evento) => set('timeout_seconds', evento.target.value)}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              {atual.updated_at
                ? `Alterada por ${atual.updated_by} em ${formatDateTime(atual.updated_at)}.`
                : 'Ainda nao gravada.'}
            </span>
            <Button type="submit" loading={salvar.isPending}>
              Salvar configuracao
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function TesteDeEnvio({ atual }: { atual: PlatformEmailSettings }) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [destino, setDestino] = useState(session?.user.email ?? '');
  const [resultado, setResultado] = useState<PlatformEmailTestResult | null>(null);

  const testar = useMutation({
    mutationFn: () => platformEmail.test(destino.trim()),
    onMutate: () => setResultado(null),
    onSuccess: (r) => {
      setResultado(r);
      void queryClient.invalidateQueries({ queryKey: ['platform', 'email'] });
    },
  });
  const erro = testar.error instanceof ApiError ? testar.error : null;

  return (
    <Card>
      <CardHeader
        title="E-mail de teste"
        description="Envia uma mensagem pela configuracao gravada, ligada ou nao. Grave antes de testar uma mudanca."
      />
      <CardBody className="flex flex-col gap-4">
        {!atual.host ? (
          <Alert tone="info">Grave o servidor e o remetente antes de testar.</Alert>
        ) : null}
        <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
          <Field
            label="Enviar o teste para"
            type="email"
            value={destino}
            help={false}
            error={fieldErrorOf(erro, 'to')}
            onChange={(evento) => setDestino(evento.target.value)}
          />
          <Button
            variant="secondary"
            loading={testar.isPending}
            onClick={() => testar.mutate()}
          >
            Enviar e-mail de teste
          </Button>
        </div>
        {testar.isError && !fieldErrorOf(erro, 'to') ? (
          <Alert tone="danger" title="Teste nao enviado">
            {testar.error.message}
          </Alert>
        ) : null}
        {resultado?.ok ? (
          <Alert tone="success" title="E-mail de teste enviado">
            {resultado.message} Confira a caixa de entrada (e a de spam).
          </Alert>
        ) : null}
        {resultado && !resultado.ok ? (
          <Alert tone="danger" title="O servidor nao aceitou o envio">
            {resultado.message}
          </Alert>
        ) : null}
      </CardBody>
    </Card>
  );
}

function UltimosEnvios() {
  const consulta = useQuery({
    queryKey: ['platform', 'email', 'envios'],
    queryFn: ({ signal }) => platformEmail.deliveries(signal),
  });
  const colunas: Column<PlatformEmailDelivery>[] = [
    {
      key: 'quando',
      header: 'Quando',
      render: (e) => (
        <span className="tabular-nums">{formatDateTime(e.created_at)}</span>
      ),
    },
    { key: 'tipo', header: 'Tipo', render: (e) => tiposDeEmail[e.kind] ?? e.kind },
    { key: 'para', header: 'Para', render: (e) => e.recipient_masked },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (e) =>
        e.status === 'enviado' ? (
          <Badge tone="success">Enviado</Badge>
        ) : (
          <Badge tone="danger">Falhou</Badge>
        ),
    },
    {
      key: 'motivo',
      header: 'Motivo',
      hideBelow: 'md',
      render: (e) => <span className="text-ink-600">{e.failure_message}</span>,
    },
  ];
  return (
    <Card>
      <CardHeader
        title="Ultimos envios"
        description="Os cinquenta envios mais recentes, com o destinatario mascarado. A falha nao desfaz a operacao que pediu o e-mail."
      />
      {consulta.isError ? (
        <CardBody>
          <Alert tone="danger" title="Nao foi possivel carregar os envios">
            {consulta.error.message}
          </Alert>
        </CardBody>
      ) : (
        <DataTable
          columns={colunas}
          items={consulta.data ?? []}
          keyOf={(e) => e.id}
          loading={consulta.isPending}
          empty="Nenhum e-mail enviado ainda."
        />
      )}
    </Card>
  );
}
